import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

/** Official English country names, sorted, for every country picker. */
export const COUNTRY_OPTIONS: readonly string[] = Object.values(
  countries.getNames("en", { select: "official" })
).sort((a, b) => a.localeCompare(b));

/** Nationalities that don't start with the country's name. */
const DEMONYMS: Record<string, string> = {
  american: "US",
  british: "GB",
  dutch: "NL",
  filipino: "PH",
  filipina: "PH",
  french: "FR",
  irish: "IE",
  swiss: "CH",
  welsh: "GB",
  scottish: "GB",
  english: "GB",
  greek: "GR",
  thai: "TH",
  turkish: "TR",
  polish: "PL",
  spanish: "ES",
  danish: "DK",
  swedish: "SE",
  finnish: "FI",
  norwegian: "NO",
  peruvian: "PE",
  vietnamese: "VN",
  "south korean": "KR",
  korean: "KR",
  "north korean": "KP"
};

const official = (alpha2: string | undefined): string | null =>
  (alpha2 && countries.getName(alpha2, "en", { select: "official" })) || null;

/**
 * The picker's country name for what a document printed: a name ("NIGERIA"),
 * an ISO code ("NGA", "NG") or a nationality ("NIGERIAN"). Null when unsure.
 */
export const matchCountry = (
  text: string | null | undefined
): string | null => {
  const raw = (text ?? "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (/^[A-Z]{3}$/.test(upper)) {
    const hit = official(countries.alpha3ToAlpha2(upper));
    if (hit) return hit;
  }
  if (/^[A-Z]{2}$/.test(upper) && countries.isValid(upper))
    return official(upper);
  const byName = official(countries.getAlpha2Code(raw, "en"));
  if (byName) return byName;
  const lower = raw.toLowerCase();
  const known = official(DEMONYMS[lower]);
  if (known) return known;
  const byOption = COUNTRY_OPTIONS.find((c) => c.toLowerCase() === lower);
  if (byOption) return byOption;
  if (lower.length < 5) return null;
  // "Nigerian" -> Nigeria (the longest name it starts with beats Niger).
  let best: string | null = null;
  for (const name of COUNTRY_OPTIONS) {
    const stem = name.toLowerCase().slice(0, Math.max(4, name.length - 1));
    if (lower.startsWith(stem) && (!best || name.length > best.length)) {
      best = name;
    }
  }
  return best;
};
